import { DesignNode } from './design-node';
import { PointNode } from './utils/point-node';

type NearestPoint = {
  node: PointNode | null;
  distance: number;
};

type KDTreeNode = {
  point: PointNode;
  left: KDTreeNode | null;
  right: KDTreeNode | null;
  deleted: boolean;
};

const axes = ['x', 'y', 'z'] as const;

function getAxis(depth: number) {
  return axes[depth % axes.length];
}

/**
 * Builds up a KDTree based on a design node.
 * This allows for efficient searching of nearby nodes when dealing with a large
 * number of nodes in the model.
 */
export class DesignNodeKDTree {
  root: KDTreeNode | null;

  constructor(designNode: DesignNode, depth = 0) {
    this.root = this.buildTree(designNode.pointNodes, depth);
  }

  private buildTree(points: PointNode[], depth: number): KDTreeNode | null {
    if (points.length === 0) {
      return null;
    }

    const axis = getAxis(depth);

    points.sort((a, b) => a.vertex[axis] - b.vertex[axis]);

    const median = Math.floor(points.length / 2);
    const node: KDTreeNode = {
      point: points[median],
      deleted: false,
      left: null,
      right: null,
    };

    node.left = this.buildTree(points.slice(0, median), depth + 1);
    node.right = this.buildTree(points.slice(median + 1), depth + 1);

    return node;
  }

  nearest(
    point: PointNode,
    depth = 0,
    best: NearestPoint | null = null,
    node: KDTreeNode | null = this.root,
  ): NearestPoint | null {
    if (node === null) {
      return best;
    }

    if (!node.deleted) {
      const axis = getAxis(depth);

      let nextBranch: KDTreeNode | null = null;
      let oppositeBranch: KDTreeNode | null = null;

      if (point.vertex[axis] < node.point.vertex[axis]) {
        nextBranch = node.left;
        oppositeBranch = node.right;
      } else {
        nextBranch = node.right;
        oppositeBranch = node.left;
      }

      const distance = point.vertex.distanceTo(node.point.vertex);
      if (best === null || distance < best.distance) {
        best = { node: node.point, distance: distance };
      }

      best = this.nearest(point, depth + 1, best, nextBranch);

      if (
        oppositeBranch !== null &&
        Math.abs(point.vertex[axis] - node.point.vertex[axis]) < (best ? best.distance : Infinity)
      ) {
        best = this.nearest(point, depth + 1, best, oppositeBranch);
      }
    }

    return best;
  }

  rangeSearch(
    point: PointNode,
    distance: number,
    depth = 0,
    node: KDTreeNode | null = this.root,
    results: NearestPoint[] = [],
  ): NearestPoint[] {
    if (node === null) {
      return results;
    }

    const axis = getAxis(depth);

    const currentDistance = point.vertex.distanceTo(node.point.vertex);

    if (currentDistance <= distance && !node.deleted) {
      results.push({ node: node.point, distance: currentDistance });
    }

    if (point.vertex[axis] - distance < node.point.vertex[axis]) {
      results = this.rangeSearch(point, distance, depth + 1, node.left, results);
    }

    if (point.vertex[axis] + distance > node.point.vertex[axis]) {
      results = this.rangeSearch(point, distance, depth + 1, node.right, results);
    }

    return results;
  }

  softDelete(point: PointNode, node: KDTreeNode | null = this.root, depth = 0): boolean {
    if (node === null) {
      return false;
    }

    const axis = axes[depth % axes.length];

    if (node.point === point) {
      node.deleted = true;
      return true;
    }

    if (point.vertex[axis] < node.point.vertex[axis]) {
      return this.softDelete(point, node.left, depth + 1);
    } else {
      return this.softDelete(point, node.right, depth + 1);
    }
  }
}
